import React, { useEffect } from 'react'
import { Route, Switch } from 'react-router-dom'

import ImpersonateHint from 'ImpersonateHint'
import RedirectPage from 'RedirectPage'
import EmployeeNotice from 'account/employeeNotice'
import EmployeeSignup from 'account/employeeSignup/eligibleEmployee'
import NonEligibleEmployeeSignup from 'account/employeeSignup/nonEligibleEmployee'
import LoginController from 'account/loginController'
import { Skeleton } from 'antd'
import CsiDashboard from 'csiDashboard'
import DocReviewDashboard from 'docReviewDashboard'
import EmployeeSignupFlow from 'employeeSignup/eligibleEmployeeSignup'
import NonEligibleEmployeeSignupFlow from 'employeeSignup/nonEligibleEmployeeSignup'
import IchraDecisionTools from 'ichraDecisionTool/Routes'
import { SIGNUP_COMPLETE_PATH } from 'ichraDecisionTool/prospectSignup/SignupComplete'
import NonEligibleEmployeeDashboard from 'nonEligibleEmployeeDashboard'
import { addInspectlet } from 'scripts/addInspectlet'
import { loadPendo } from 'scripts/loadPendo'
import { loadZendeskWidget } from 'scripts/loadZendeskWidget'

import getLoginUser, { User } from 'core/application/account/loginUser'
import config from 'core/application/commons/config'
import { sessionStore } from 'core/application/commons/localStorage'

import useLoginUser from 'common/hooks/useLoginUser'
import useQueryRequest from 'common/hooks/useQueryRequest'

import NotFound from 'errorPages/NotFound'

import ForgetPassword from './account/forgetPassword'
import Login from './account/login/Login'
import SetNewQsehraPassword from './account/setNewQsehraPassword'
import Signup from './account/signup/Signup'
import AdminDashboard from './adminDashboard'
import AdminSignUp from './adminSignUp'
import useSharedState, { SharedStateKeys } from './common/hooks/useSharedState'
import { ROUTE_PATHS } from './common/routePaths'
import EligibleEmployeeDashboard from './employeeDashboard'

import './App.less'
//
import 'antd/dist/reset.css'

interface AppProps {}

const isProduction = window.location.href.includes('app.peoplekeep.com')

const App: React.FC<AppProps> = () => {
  const [, setIsProduction] = useSharedState<boolean>(SharedStateKeys.IsProduction)

  useEffect(() => {
    recurly.configure({ publicKey: config.recurlyKey })
  }, [])

  useEffect(() => {
    setIsProduction(isProduction)
  }, [])

  return (
    <Skeleton loading={false} active>
      <ImpersonateHint />
      <Switch>
        <Route exact path="/" component={RedirectPage} />
        <Route path={ROUTE_PATHS.SIGNUP_PATH} component={Signup} />
        <Route path={ROUTE_PATHS.PASSWORD_RESET_REQUIRED_PATH} component={SetNewQsehraPassword} />
        <Route exact path={ROUTE_PATHS.LOGIN_PATH} component={Login} />
        <Route path={ROUTE_PATHS.ELIGIBLE_EMPLOYEE_CREATE_ACCOUNT} component={EmployeeSignup} />
        <Route path={ROUTE_PATHS.ELIGIBLE_EMPLOYEE_SIGNUP_FLOW} component={EmployeeSignupFlow} />
        <Route path={ROUTE_PATHS.NON_ELIGIBLE_EMPLOYEE_SIGNUP} component={NonEligibleEmployeeSignup} />
        <Route path={ROUTE_PATHS.NON_ELIGIBLE_EMPLOYEE_SIGNUP_FLOW} component={NonEligibleEmployeeSignupFlow} />
        <Route path={ROUTE_PATHS.FORGOT_PASSWORD_PATH} component={ForgetPassword} />
        <Route path={ROUTE_PATHS.ELIGIBLE_EMPLOYEE_NOTICE} component={EmployeeNotice} />
        <Route path={``} component={() => <PrivateRoutes />} />
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </Skeleton>
  )
}

const PrivateRoutes = () => {
  const [user] = useQueryRequest(null, () => getLoginUser())
  const [loginUser, setLoginUser] = useLoginUser()

  useEffect(() => {
    setLoginUser(user)

    if (user && shoudDisplayZendeskWidget()) {
      loadZendeskWidget(config.zendeskKey[user.productId])
    }

    if (shouldRecordInspectletSession(user)) {
      addInspectlet(config.inspectletKey, user.email)
    }

    if (user && isProduction) {
      loadPendo(user)
    }
  }, [user])

  const isOnInternalTools = (): boolean => {
    const location = window.location.href
    return (
      location.includes(ROUTE_PATHS.ICHRA_DECISION_TOOL_PATH) ||
      location.includes(ROUTE_PATHS.PROSPECT_WELCOME_PATH) ||
      location.includes(ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH) ||
      location.includes(ROUTE_PATHS.CSI_DASHBOARD_PATH) ||
      location.includes(ROUTE_PATHS.CHOOSE_APP)
    )
  }

  const shoudDisplayZendeskWidget = (): boolean => {
    const location = window.location.href

    return !(isOnInternalTools() || location.includes(SIGNUP_COMPLETE_PATH))
  }

  const shouldRecordInspectletSession = (user: User): boolean => {
    const location = window.location.href
    const routeIsAllowedToBeRecorded = !isOnInternalTools()

    return (
      (config.env === 'production' || config.env === 'QA') &&
      !sessionStore.getSessionToken() &&
      user !== null &&
      routeIsAllowedToBeRecorded
    )
  }

  return (
    <>
      {loginUser && (
        <Switch>
          <Route path={ROUTE_PATHS.ADMIN_SIGNUP_PATH} component={AdminSignUp} />
          <Route path={ROUTE_PATHS.ADMIN_DASHBOARD_PATH} component={AdminDashboard} />
          <Route path={ROUTE_PATHS.NON_ELIGIBLE_EMPLOYEE_SIGNUP_FLOW} component={NonEligibleEmployeeSignupFlow} />
          <Route path={ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD} component={EligibleEmployeeDashboard} />
          <Route path={ROUTE_PATHS.NON_ELIGIBLE_EMPLOYEE_DASHBOARD} component={NonEligibleEmployeeDashboard} />
          <Route path={ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH} component={DocReviewDashboard} />
          <Route path={ROUTE_PATHS.CSI_DASHBOARD_PATH} component={CsiDashboard} />
          <Route path={ROUTE_PATHS.ICHRA_DECISION_TOOL_PATH} component={IchraDecisionTools} />
          <Route path={ROUTE_PATHS.CHOOSE_APP} component={LoginController} />
        </Switch>
      )}
    </>
  )
}

export default App
